import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

// Types
interface Batch {
  id: string;
  name: string;
  class: string; // IX|X|XI|XII
}
interface Chapter {
  id: string;
  class: string;
  chapterName: string;
}

interface HomeworkItem {
  id: string;
  class: string; // class selector
  batchId: string;
  batchName?: string;
  chapterId: string;
  chapterName?: string;
  title: string;
  description?: string;
  contentType: "text" | "link" | "image";
  textContent?: string;
  linkUrl?: string;
  imageUrl?: string;
  dueDate?: string; // ISO string (date or datetime)
  createdAt?: any;
}

const classOptions = ["IX", "X", "XI", "XII"];

const Homework: React.FC = () => {
  // Form state
  const [selectedClass, setSelectedClass] = useState("");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [batchId, setBatchId] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contentType, setContentType] = useState<"text" | "link" | "image">(
    "text"
  );
  const [textContent, setTextContent] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [dueDate, setDueDate] = useState("");

  // List state
  const [homeworks, setHomeworks] = useState<HomeworkItem[]>([]);
  const [filterClass, setFilterClass] = useState<string>("");

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Load batches, chapters when class changes
  useEffect(() => {
    const loadForClass = async () => {
      if (!selectedClass) {
        setBatches([]);
        setChapters([]);
        setBatchId("");
        setChapterId("");
        return;
      }
      try {
        // batches collection filtered by "class"
        const bq = query(collection(db, "batches"), where("class", "==", selectedClass));
        const bSnap = await getDocs(bq);
        const bList: Batch[] = bSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setBatches(bList);

        // chapters collection filtered by class
        const cq = query(collection(db, "chapters"), where("class", "==", selectedClass));
        const cSnap = await getDocs(cq);
        const cList: Chapter[] = cSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setChapters(cList);
        setBatchId("");
        setChapterId("");
      } catch (e) {
        console.error(e);
        setError("Failed to load batches/chapters for selected class.");
      }
    };
    loadForClass();
  }, [selectedClass]);

  // Live list of homework (optionally filter by class)
  useEffect(() => {
    const qBase = collection(db, "homework");
    const unsub = onSnapshot(query(qBase, orderBy("createdAt", "desc")), async (snap) => {
      const rows: HomeworkItem[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      setHomeworks(rows);
    });
    return () => unsub();
  }, []);

  const filteredHomeworks = useMemo(() => {
    return filterClass ? homeworks.filter((h) => h.class === filterClass) : homeworks;
  }, [homeworks, filterClass]);

  const resetForm = () => {
    setSelectedClass("");
    setBatchId("");
    setChapterId("");
    setTitle("");
    setDescription("");
    setContentType("text");
    setTextContent("");
    setLinkUrl("");
    setImageFile(null);
    setDueDate("");
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!selectedClass || !batchId || !chapterId || !title) {
      setError("Please fill class, batch, chapter and title.");
      return;
    }
    if (contentType === "text" && !textContent.trim()) {
      setError("Please enter homework text content.");
      return;
    }
    if (contentType === "link" && !linkUrl.trim()) {
      setError("Please provide a link URL.");
      return;
    }
    if (contentType === "image" && !imageFile && !editingId) {
      setError("Please upload an image for homework.");
      return;
    }

    try {
      setLoading(true);
      let imageUrl: string | undefined = undefined;
      if (contentType === "image" && imageFile) {
        const cloudName = "dhxiqrn0d"; // from existing components
        const uploadPreset = "marazsir";
        const url = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`;
        const fd = new FormData();
        fd.append("file", imageFile);
        fd.append("upload_preset", uploadPreset);
        const res = await fetch(url, { method: "POST", body: fd });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Cloudinary upload failed (${res.status}): ${txt}`);
        }
        const data = await res.json();
        imageUrl = data.secure_url;
      }

      const batchName = batches.find((b) => b.id === batchId)?.name || "";
      const chapterName = chapters.find((c) => c.id === chapterId)?.chapterName || "";

      // Build payload without undefined fields
      const payload: any = {
        class: selectedClass,
        batchId,
        batchName,
        chapterId,
        chapterName,
        title,
        contentType,
        createdAt: serverTimestamp(),
      };
      if (description) payload.description = description;
      if (dueDate) payload.dueDate = dueDate;
      if (contentType === "text" && textContent) payload.textContent = textContent;
      if (contentType === "link" && linkUrl) payload.linkUrl = linkUrl;
      // For image: only set imageUrl if a new upload happened
      if (contentType === "image" && imageUrl) payload.imageUrl = imageUrl;

      if (editingId) {
        await updateDoc(doc(db, "homework", editingId), payload as any);
        setSuccess("Homework updated successfully.");
      } else {
        await addDoc(collection(db, "homework"), payload as any);
        setSuccess("Homework created successfully.");
      }
      resetForm();
    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to save homework.");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (item: HomeworkItem) => {
    setEditingId(item.id);
    setSelectedClass(item.class);
    setBatchId(item.batchId);
    setChapterId(item.chapterId);
    setTitle(item.title);
    setDescription(item.description || "");
    setContentType(item.contentType);
    setTextContent(item.textContent || "");
    setLinkUrl(item.linkUrl || "");
    setImageFile(null);
    setDueDate(item.dueDate || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this homework?")) return;
    try {
      await deleteDoc(doc(db, "homework", id));
    } catch (e) {
      console.error(e);
      setError("Failed to delete.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto bg-white p-6 rounded shadow mt-4">
      <h2 className="text-2xl font-bold mb-4">Homework Management</h2>

      {success && (
        <div className="mb-3 p-2 bg-green-100 text-green-700 rounded">{success}</div>
      )}
      {error && (
        <div className="mb-3 p-2 bg-red-100 text-red-700 rounded">{error}</div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              required
            >
              <option value="">Select Class</option>
              {classOptions.map((c) => (
                <option key={c} value={c}>{`Class ${c}`}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              required
              disabled={!selectedClass}
            >
              <option value="">{selectedClass ? "Select Batch" : "Select class first"}</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chapter</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              required
              disabled={!selectedClass}
            >
              <option value="">{selectedClass ? "Select Chapter" : "Select class first"}</option>
              {chapters.map((ch) => (
                <option key={ch.id} value={ch.id}>{ch.chapterName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              className="w-full border rounded px-3 py-2"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Homework title"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={contentType}
              onChange={(e) => setContentType(e.target.value as any)}
            >
              <option value="text">Written (Text)</option>
              <option value="link">Link</option>
              <option value="image">Image</option>
            </select>
          </div>

          {contentType === "text" && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Homework Text</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={3}
                value={textContent}
                onChange={(e) => setTextContent(e.target.value)}
                placeholder="Write the homework details here..."
              />
            </div>
          )}

          {contentType === "link" && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Homework Link</label>
              <input
                type="url"
                className="w-full border rounded px-3 py-2"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          )}

          {contentType === "image" && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">If you're editing and leave this empty, existing image (if any) will be kept.</p>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
            >
              Cancel Edit
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-60"
          >
            {loading ? "Saving..." : editingId ? "Update Homework" : "Create Homework"}
          </button>
        </div>
      </form>

      {/* List */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-semibold">Homework List</h3>
          <select
            className="border rounded px-3 py-2"
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
          >
            <option value="">All Classes</option>
            {classOptions.map((c) => (
              <option key={c} value={c}>{`Class ${c}`}</option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-3 py-2">Class</th>
                <th className="border px-3 py-2">Batch</th>
                <th className="border px-3 py-2">Chapter</th>
                <th className="border px-3 py-2">Title</th>
                <th className="border px-3 py-2">Due</th>
                <th className="border px-3 py-2">Type</th>
                <th className="border px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHomeworks.length === 0 ? (
                <tr>
                  <td className="text-center text-gray-500 py-4" colSpan={7}>
                    No homework found.
                  </td>
                </tr>
              ) : (
                filteredHomeworks.map((hw) => (
                  <tr key={hw.id}>
                    <td className="border px-3 py-2">{hw.class}</td>
                    <td className="border px-3 py-2">{hw.batchName || hw.batchId}</td>
                    <td className="border px-3 py-2">{hw.chapterName || hw.chapterId}</td>
                    <td className="border px-3 py-2">{hw.title}</td>
                    <td className="border px-3 py-2">{hw.dueDate ? (() => { try { return new Date(hw.dueDate).toLocaleDateString(); } catch { return hw.dueDate; } })() : "--"}</td>
                    <td className="border px-3 py-2">{hw.contentType}</td>
                    <td className="border px-3 py-2">
                      <button
                        className="bg-yellow-500 text-white px-2 py-1 rounded mr-2 hover:bg-yellow-600"
                        onClick={() => startEdit(hw)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                        onClick={() => handleDelete(hw.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Homework;
